import * as React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Dropzone } from "@/components/ui/dropzone";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AmountInput } from "@/components/admin/AmountInput";
import { DatePicker } from "@/components/DatePicker";
import { CURRENCIES } from "@/lib/countries";
import {
  ApiError,
  analyzeExpenseFile,
  checkExpenseDuplicate,
  deleteExpense,
  expenseFileUrl,
  fetchExpenseCategories,
  updateExpense,
  uploadExpense
} from "../../api";
import {
  BillFields,
  ClaimMethod,
  ExpenseCategory,
  ExpenseLineItem,
  ExtractedBillFields,
  OtherBillFieldValues
} from "../../types";
import {
  OtherBillInputs,
  missingOtherBillField,
  otherBillPayload,
  otherBillSummary,
  otherBillValuesEqual,
  otherBillValuesOf
} from "@/components/requests/OtherBillFields";

// Below this confidence (or an outright isLegible: false), a file's
// extracted fields are treated as needing a human look before it can be
// added - see PendingBillState below.
const CONFIDENCE_THRESHOLD = 0.6;

// Matches the backend's Flask MAX_CONTENT_LENGTH (app.py) - rejected here,
// client-side, so an oversized file never reaches analyzeExpenseFile's
// upload at all rather than round-tripping to the server just to be
// bounced by it.
const MAX_BILL_FILE_BYTES = 50 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

let nextLocalId = 0;

// 'ok' also covers "AI never ran / came back confident" - the confirmation
// gate only kicks in once an actual low-confidence result comes back.
// 'checking-duplicate': fields are already extracted and shown (read-only,
// same fieldset-disabled treatment as 'needs-confirmation'), but the card
// deliberately does NOT flip to 'ok' until the duplicate check has actually
// come back - showing a fully "clean" card before that check resolves let
// an employee glance at it, see nothing wrong, and move on a moment before
// a duplicate warning would have appeared.
type PendingBillState =
  | "analyzing"
  | "checking-duplicate"
  | "ok"
  | "needs-confirmation"
  | "failed";

interface PendingBill {
  localId: string;
  // The same File is shared by every card split out of one uploaded file
  // (see runAnalysis) - each card still uploads it independently via its
  // own uploadExpense() call, since the backend has no "one file, many
  // expenses" primitive; each resulting expense line item gets its own
  // blob copy of the same bytes. null for a manually-added bill with no
  // receipt (see newManualBill) - the backend now accepts that (see
  // requests_api.py's upload_expense).
  file: File | null;
  category: ExpenseCategory;
  amount: number;
  currency: string;
  description: string;
  date: string;
  // Not shown as its own field in this step's form - carried through from
  // AI extraction purely so it reaches ExpenseLineItem.vendor and
  // strengthens later duplicate checks (see check_expense_duplicate in
  // requests_api.py). Always null for a manual bill - see fieldsOf/
  // newManualBill; manual bills are never duplicate-checked at all.
  vendor: string | null;
  state: PendingBillState;
  note: string | null;
  duplicateWarning: string | null;
  // Stamped (from nextCheckSeq) each time this card's duplicate check
  // starts. A check compares the card only against cards stamped EARLIER, so
  // of two identical cards the first is the original and the later one is
  // the duplicate - whichever order their (concurrent) AI analyses happen to
  // finish in, and no matter that both checks are in flight at once. null
  // until a check has started (a card still being analysed has no fields to
  // compare yet).
  checkSeq: number | null;
  // True while a user-requested re-check (the refresh icon) is running - kept
  // apart from `state` so it works the same for a card in any state.
  rechecking: boolean;
  // Other reimbursement bills only (see the billFields prop) - start/end
  // place, purpose, mode of transport, buyer. Empty on a travel bill.
  other: OtherBillFieldValues;
}

// Module-level (like nextLocalId) so stamps stay unique and increasing across
// every card, whichever ExpensesStep render started the check.
let nextCheckSeq = 0;

function newPendingBill(file: File, defaultCurrency: string, defaultCategory: string): PendingBill {
  nextLocalId += 1;
  return {
    localId: `local-${nextLocalId}`,
    file,
    category: defaultCategory,
    amount: 0,
    currency: defaultCurrency,
    description: "",
    date: "",
    vendor: null,
    state: "analyzing",
    note: null,
    duplicateWarning: null,
    checkSeq: null,
    rechecking: false,
    other: {}
  };
}

// "+ Add bill manually" - no file, no AI analysis, the employee types
// every field themselves. Starts 'ok' (not 'analyzing') since there's
// nothing to analyze.
function newManualBill(defaultCurrency: string, defaultCategory: string): PendingBill {
  nextLocalId += 1;
  return {
    localId: `local-${nextLocalId}`,
    file: null,
    category: defaultCategory,
    amount: 0,
    currency: defaultCurrency,
    description: "",
    date: "",
    vendor: null,
    state: "ok",
    note: null,
    duplicateWarning: null,
    checkSeq: null,
    rechecking: false,
    other: {}
  };
}

// The refresh icon on a pending card - re-runs the duplicate check on demand.
// Disabled while a check is already running, while the card is still being
// read by the AI, and while there's no amount yet to compare.
function RecheckButton({
  bill,
  onClick
}: {
  bill: PendingBill;
  onClick: () => void;
}) {
  const running = bill.rechecking || bill.state === "checking-duplicate";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={onClick}
      disabled={running || bill.state === "analyzing" || bill.amount <= 0}
      title="Re-check for duplicates"
      aria-label="Re-check for duplicates"
    >
      <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
    </Button>
  );
}

// What the duplicate check compares for a card, from its current (possibly
// edited) values - used for manual bills and for re-checks, where there's no
// fresh AI extraction to hand over.
function fieldsOf(bill: PendingBill): ExtractedBillFields {
  return {
    vendor: bill.vendor,
    amount: bill.amount,
    currency: bill.currency,
    date: bill.date || null,
    category: bill.category,
    description: bill.description || null,
    confidence: 1,
    isLegible: true
  };
}

// Drop one or more bills at once. Each file gets its own card, auto-analyzed
// by the fill-fields agent and auto-checked for duplicates against the
// employee's other bills (agents/bill_analysis.py) - the user reviews/edits
// every card, then "Add all bills" commits them together. A low-confidence
// read or a confident duplicate match blocks that one card (not the whole
// batch) until explicitly acknowledged. Used for Reimbursement bills (the
// claim itself) and Fixed Allowance expenses (optional, tracking only).
export function ExpensesStep({
  travelRequestId,
  expenses,
  onChanged,
  defaultCurrency,
  claimMethod,
  onBusyChange,
  onPendingChange,
  onEditingChange,
  billFields = "travel"
}: {
  travelRequestId: string;
  expenses: ExpenseLineItem[];
  onChanged: (expenses: ExpenseLineItem[]) => void;
  /** Seeds each new bill card's currency dropdown (the request's own
   *  overall currency, e.g. from the destination country) - every card
   *  stays independently editable/overridable from there. */
  defaultCurrency: string;
  /** Fixed Allowance bills are tracking-only, with no policy limits to
   *  check against, so any currency is allowed for them; Reimbursement
   *  bills are limited to the currencies policy has limits for. */
  claimMethod: ClaimMethod;
  /** Fired whenever a pending bill card is actively being processed
   *  (AI field extraction or a duplicate check still in flight). The wizard
   *  uses this to disable its Next button while analysis is running, so a
   *  click-through can't navigate away from the step and silently discard
   *  the not-yet-added card being analysed. */
  onBusyChange?: (busy: boolean) => void;
  /** How many bill cards are on screen but not yet added with "Add bill" -
   *  the wizard refuses Next while any are, since leaving the step would
   *  silently drop them. */
  onPendingChange?: (count: number) => void;
  /** Fired whenever an ALREADY-ADDED bill's inline edit form is open
   *  (editingId below) - distinct from onPendingChange, which is about
   *  cards not yet added at all. Without this, clicking the pencil icon,
   *  changing a field, then clicking Next (without clicking the edit
   *  form's own Save) silently discarded the in-progress edit and moved on
   *  - the same "leaving the step drops unsaved work" gap onPendingChange
   *  already closes for a brand-new card, just for editing an existing
   *  one instead. The wizard disables Next while this is true. */
  onEditingChange?: (editing: boolean) => void;
  /** Which bill fields to show - "travel" (the default, every travel
   *  request) or an Other reimbursement claim type: City travel adds start/
   *  end place, purpose, mode of transport and buyer (and drops the
   *  description); Miscellaneous adds buyer. */
  billFields?: BillFields;
}) {
  const otherType = billFields === "travel" ? null : billFields;
  const [pending, setPending] = React.useState<PendingBill[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<string | null>(null);

  // A bill can be claimed in any currency - one with no configured policy
  // limit simply has nothing to compare against (same as every Fixed
  // Allowance bill, which never has a limit either), so it's never flagged
  // as exceeding anything rather than being blocked from upload at all.

  // Admin-configured (Policies.tsx's "Expense Categories" panel) - fetched
  // here rather than threaded down from NewRequest.tsx/ReviewRequestDialog.tsx,
  // the same self-fetch convention this step already uses for its own
  // request-scoped config.
  const [categories, setCategories] = React.useState<string[]>([]);
  React.useEffect(() => {
    fetchExpenseCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);
  const defaultCategory = categories[0] ?? "Other";

  // The CURRENT list of pending cards, readable inside callbacks without
  // making them depend on (and re-create on every change of) `pending` -
  // runDuplicateCheck reads it to build the sibling candidate list at call
  // time. It is updated SYNCHRONOUSLY by updatePending below, not from an
  // effect after the next render: with several files analysed at once, one
  // card's duplicate check routinely starts in the same tick as another
  // card's fields land, and a ref that only catches up after the render
  // meant that check compared against a stale list - neither identical card
  // saw the other, so neither was flagged.
  const pendingRef = React.useRef<PendingBill[]>([]);
  const updatePending = React.useCallback(
    (fn: (prev: PendingBill[]) => PendingBill[]) => {
      pendingRef.current = fn(pendingRef.current);
      setPending(pendingRef.current);
    },
    []
  );

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editCategory, setEditCategory] =
    React.useState<ExpenseCategory>(defaultCategory);
  const [editAmount, setEditAmount] = React.useState(0);
  const [editCurrency, setEditCurrency] = React.useState(defaultCurrency);
  const [editDescription, setEditDescription] = React.useState("");
  const [editDate, setEditDate] = React.useState("");
  const [editOther, setEditOther] = React.useState<OtherBillFieldValues>({});
  const [editOtherOriginal, setEditOtherOriginal] = React.useState<OtherBillFieldValues>({});
  const [savingEdit, setSavingEdit] = React.useState(false);
  // Snapshot of the values startEdit loaded, kept only to detect whether the
  // user actually changed anything - Save stays disabled otherwise, rather
  // than being enabled the instant the row opens for editing.
  const [editOriginal, setEditOriginal] = React.useState<{
    category: ExpenseCategory;
    amount: number;
    currency: string;
    description: string;
    date: string;
  } | null>(null);
  const editIsDirty =
    !!editOriginal &&
    (editCategory !== editOriginal.category ||
      editAmount !== editOriginal.amount ||
      editCurrency !== editOriginal.currency ||
      editDescription !== editOriginal.description ||
      editDate !== editOriginal.date ||
      !otherBillValuesEqual(editOther, editOtherOriginal));

  const patchPending = React.useCallback(
    (localId: string, patch: Partial<PendingBill>) => {
      updatePending((prev) =>
        prev.map((p) => (p.localId === localId ? { ...p, ...patch } : p))
      );
    },
    [updatePending]
  );

  const removePending = (localId: string) => {
    updatePending((prev) => prev.filter((p) => p.localId !== localId));
  };

  // Runs the duplicate check for one card's current fields, in place -
  // shared by the single-bill, multi-bill, manual and re-check paths. Also
  // compares against the OTHER pending (not-yet-added) cards in this same
  // batch - without that, two copies of the same file dropped into the
  // dropzone in one sitting would never catch each other, since neither is
  // "already uploaded" until one is actually committed.
  //
  // Which siblings count: only cards whose own check started EARLIER (see
  // PendingBill.checkSeq), and that aren't themselves flagged as duplicates.
  // The first of two identical cards is the original and the later one gets
  // flagged - deterministically, regardless of which analysis finished first
  // or that both checks are running at once. (Comparing against every card
  // would flag BOTH, leaving neither addable; a flagged card is no
  // "original" to be a duplicate of, which also means removing the original
  // and re-checking releases the other.)
  const runDuplicateCheck = React.useCallback(
    async (localId: string, fields: ExtractedBillFields) => {
      // A card sitting in 'checking-duplicate' (fields already extracted and
      // shown, but not yet promoted to 'ok') settles to 'ok' the moment this
      // check resolves - success, no-duplicate, or failure alike, matching
      // the fail-open contract below. A card that's 'needs-confirmation' for
      // an unrelated reason (low-confidence extraction) is a separate,
      // still-unresolved gate and must NOT be overridden here - only advance
      // out of 'checking-duplicate' specifically. Also ends a user-requested
      // re-check's spinner.
      const settle = () =>
        updatePending((prev) =>
          prev.map((p) =>
            p.localId === localId
              ? {
                  ...p,
                  rechecking: false,
                  state: p.state === "checking-duplicate" ? "ok" : p.state
                }
              : p
          )
        );
      // Nothing to compare yet (the extractor found no amount) - still settle,
      // or the card would sit in 'checking-duplicate' with its fields locked.
      if (!fields.amount) {
        settle();
        return;
      }
      const checkSeq = ++nextCheckSeq;
      // A fresh check starts from a clean slate: any warning from an earlier
      // check is dropped (a re-check may well clear it).
      patchPending(localId, { checkSeq, duplicateWarning: null });
      // Cards split out of one upload (same File object) are lines of one
      // statement, never duplicates of each other - a folio's two identical
      // 1,800,000 room nights would otherwise block the second. The same file
      // dropped again is a new File object, so it is still compared.
      const ownFile = pendingRef.current.find((p) => p.localId === localId)?.file ?? null;
      const siblingCandidates: ExtractedBillFields[] = pendingRef.current
        .filter(
          (p) =>
            p.localId !== localId &&
            !(ownFile !== null && p.file === ownFile) &&
            p.amount > 0 &&
            p.checkSeq !== null &&
            p.checkSeq < checkSeq &&
            !p.duplicateWarning
        )
        .map(fieldsOf);
      try {
        const verdict = await checkExpenseDuplicate(
          travelRequestId,
          fields,
          siblingCandidates
        );
        if (verdict.isDuplicate) {
          patchPending(localId, {
            duplicateWarning: verdict.matchedInBatch
              ? `Looks like the same bill as another one you've just added in this batch. ${verdict.reason}`
              : verdict.matchedCrossEmployee
                ? `This looks like a bill already claimed on another employee's request. ${verdict.reason}`
                : verdict.matchedFilename
                  ? `Looks like the same bill as "${verdict.matchedFilename}", already uploaded elsewhere. ${verdict.reason}`
                  : `Looks like a bill you've already uploaded. ${verdict.reason}`
          });
        }
      } catch {
        // Fails open by design - an infra error here never blocks upload.
      } finally {
        settle();
      }
    },
    [travelRequestId, patchPending, updatePending]
  );

  // The refresh icon on a card: run the duplicate check again from the
  // card's CURRENT values (read from the live list, so edits made since the
  // last check count) - e.g. after the original it matched has been removed,
  // or the employee corrected the amount/date. Compares against bills
  // already on record and the other cards in this batch, same as the first
  // check; a card that's no longer a duplicate becomes addable again.
  const recheckDuplicate = (localId: string) => {
    const bill = pendingRef.current.find((p) => p.localId === localId);
    if (
      !bill ||
      bill.rechecking ||
      bill.state === "analyzing" ||
      bill.state === "checking-duplicate"
    )
      return;
    patchPending(localId, { rechecking: true, duplicateWarning: null });
    void runDuplicateCheck(localId, fieldsOf(bill));
  };

  // The extractor reads no places or buyer - only a City travel bill's
  // Purpose can be prefilled, from what it read as the description (a
  // City travel bill has no description field of its own).
  const otherPrefill = React.useCallback(
    (fields: ExtractedBillFields): OtherBillFieldValues =>
      otherType === "city_travel" ? { purpose: fields.description || "" } : {},
    [otherType]
  );

  const runAnalysis = React.useCallback(
    async (bill: PendingBill) => {
      // Only called from onFilesSelected below, where every bill is built via
      // newPendingBill (always has a real File) - never newManualBill (file:
      // null). Guarded anyway since PendingBill.file is typed File | null.
      if (!bill.file) return;
      try {
        const results = await analyzeExpenseFile(travelRequestId, bill.file);

        if (results.length > 1) {
          // The file contains multiple distinct bills - replace this one
          // placeholder card with one card per bill found, all sharing the
          // same underlying file.
          const splitBills: PendingBill[] = results.map((fields) => {
            nextLocalId += 1;
            const needsReview =
              !fields.isLegible || fields.confidence < CONFIDENCE_THRESHOLD;
            return {
              localId: `local-${nextLocalId}`,
              file: bill.file,
              category: categories.includes(fields.category)
                ? (fields.category as ExpenseCategory)
                : defaultCategory,
              amount: fields.amount ?? 0,
              currency: fields.currency || bill.currency,
              description: fields.description || "",
              date: fields.date || "",
              vendor: fields.vendor || null,
              // 'checking-duplicate' (not 'ok') - runDuplicateCheck below
              // promotes this to 'ok' once it resolves, so the card never
              // shows as fully clean before that check has actually run.
              state: needsReview ? "needs-confirmation" : "checking-duplicate",
              note: needsReview
                ? "We couldn't confidently read this bill - please check the fields below."
                : null,
              duplicateWarning: null,
              checkSeq: null,
              rechecking: false,
              other: otherPrefill(fields)
            };
          });
          updatePending((prev) => {
            const withoutOriginal = prev.filter(
              (p) => p.localId !== bill.localId
            );
            const originalIndex = prev.findIndex(
              (p) => p.localId === bill.localId
            );
            return [
              ...withoutOriginal.slice(0, originalIndex),
              ...splitBills,
              ...withoutOriginal.slice(originalIndex)
            ];
          });
          results.forEach((fields, i) => {
            void runDuplicateCheck(splitBills[i].localId, fields);
          });
          return;
        }

        const fields = results[0];
        const needsReview =
          !fields.isLegible || fields.confidence < CONFIDENCE_THRESHOLD;
        patchPending(bill.localId, {
          category: categories.includes(fields.category)
            ? (fields.category as ExpenseCategory)
            : defaultCategory,
          amount: fields.amount ?? 0,
          currency: fields.currency || bill.currency,
          description: fields.description || "",
          date: fields.date || "",
          vendor: fields.vendor || null,
          other: otherPrefill(fields),
          // See the multi-bill branch above for why 'checking-duplicate' and
          // not 'ok' here.
          state: needsReview ? "needs-confirmation" : "checking-duplicate",
          note: needsReview
            ? fields.isLegible
              ? "We couldn't confidently read this bill - please check the fields below."
              : "We couldn't read this file as a bill - please fill in the fields yourself."
            : null
        });
        void runDuplicateCheck(bill.localId, fields);
      } catch {
        patchPending(bill.localId, {
          state: "failed",
          note: "Could not analyze this file - enter the details manually."
        });
      }
    },
    [travelRequestId, runDuplicateCheck, patchPending, updatePending, categories, defaultCategory, otherPrefill]
  );

  const onFilesSelected = (files: File[]) => {
    const newBills = files.map((f) => newPendingBill(f, defaultCurrency, defaultCategory));
    updatePending((prev) => [...prev, ...newBills]);
    setError(null);
    newBills.forEach((bill) => {
      void runAnalysis(bill);
    });
  };

  const onFilesRejected = (files: File[]) => {
    // Dropzone reports both an oversized file and a wrong-type one (e.g. a
    // .docx/.pptx dropped onto the box - accept="image/*,.pdf" only
    // constrains the native file-picker dialog, not a drag-and-drop) through
    // this one callback, so split them back out for an accurate message.
    const oversized = files.filter((f) => f.size > MAX_BILL_FILE_BYTES);
    const wrongType = files.filter((f) => f.size <= MAX_BILL_FILE_BYTES);
    const parts: string[] = [];
    if (oversized.length === 1) {
      parts.push(
        `"${oversized[0].name}" is ${formatFileSize(oversized[0].size)} - files over 50MB can't be uploaded.`
      );
    } else if (oversized.length > 1) {
      parts.push(`${oversized.length} files are over the 50MB limit.`);
    }
    if (wrongType.length === 1) {
      parts.push(`"${wrongType[0].name}" isn't an image or PDF.`);
    } else if (wrongType.length > 1) {
      parts.push(`${wrongType.length} files aren't images or PDFs.`);
    }
    const message = `${parts.join(" ")} Not added.`;
    setError(message);
    toast.error(message);
  };

  const addManualBill = () => {
    updatePending((prev) => [...prev, newManualBill(defaultCurrency, defaultCategory)]);
    setError(null);
  };

  const confirmLowConfidence = (localId: string) => {
    patchPending(localId, { state: "ok", note: null });
  };

  // A confirmed duplicate is a hard stop - no "add anyway" override. The
  // only way past it is to remove this card (e.g. because it really was
  // already claimed elsewhere) rather than resubmit the same bill twice.
  // Bill date is mandatory - FX conversion (see lib/fx.ts) looks up the
  // historical exchange rate for this exact date, so a bill with no date
  // can never be added.
  // An Other reimbursement bill also needs its claim type's required fields
  // (see OtherBillFields.tsx); a travel bill has none.
  const otherFieldsMissing = (p: PendingBill) =>
    otherType !== null && missingOtherBillField(otherType, p.other) !== null;
  const readyToAdd = pending.filter((p) => p.amount > 0 && !!p.date && !otherFieldsMissing(p));
  const missingDateOnly = pending.filter((p) => p.amount > 0 && !p.date && !otherFieldsMissing(p));
  const blockedCount = pending.filter(
    (p) =>
      p.state === "analyzing" ||
      p.state === "checking-duplicate" ||
      p.rechecking ||
      p.state === "needs-confirmation" ||
      !!p.duplicateWarning
  ).length;
  const canSubmit =
    pending.length > 0 &&
    blockedCount === 0 &&
    readyToAdd.length === pending.length &&
    !submitting;

  // A pending card whose AI analysis / duplicate check is still running -
  // reported up so the wizard can block Next while it's in flight (clicking
  // Next otherwise leaves the step and silently drops the card). Only the
  // actively-processing states count here; a card simply waiting to be
  // added is reported separately through onPendingChange.
  const busy = pending.some(
    (p) =>
      p.state === "analyzing" || p.state === "checking-duplicate" || p.rechecking
  );
  React.useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  // Report "not busy" on unmount so a stale `true` can't outlive this step
  // (e.g. the user navigates Back mid-analysis) and leave Next disabled on
  // another step forever.
  React.useEffect(() => {
    return () => onBusyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    onPendingChange?.(pending.length);
  }, [pending.length, onPendingChange]);
  React.useEffect(() => {
    return () => onPendingChange?.(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    onEditingChange?.(editingId !== null);
  }, [editingId, onEditingChange]);
  // Report "not editing" on unmount for the same reason onBusyChange/
  // onPendingChange do - e.g. the employee navigates Back with an edit form
  // still open, which must not leave Next disabled on another step forever.
  React.useEffect(() => {
    return () => onEditingChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitAll = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      let latest: ExpenseLineItem[] = expenses;
      for (const bill of pending) {
        const updated = await uploadExpense(travelRequestId, bill.file, {
          category: bill.category,
          amount: bill.amount,
          currency: bill.currency,
          description: bill.description || undefined,
          date: bill.date,
          vendor: bill.vendor || undefined,
          ...(otherType ? otherBillPayload(otherType, bill.other) : {})
        });
        latest = updated.expenses;
      }
      onChanged(latest);
      updatePending(() => []);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not upload one or more bills. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (expense: ExpenseLineItem) => {
    const category = expense.category;
    const amount = expense.amount;
    const currency = expense.currency || defaultCurrency;
    const description = expense.description || "";
    const date = expense.date || "";
    setEditingId(expense.id);
    setEditCategory(category);
    setEditAmount(amount);
    setEditCurrency(currency);
    setEditDescription(description);
    setEditDate(date);
    setEditOriginal({ category, amount, currency, description, date });
    const other = otherBillValuesOf(expense);
    setEditOther(other);
    setEditOtherOriginal(other);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditOriginal(null);
  };

  const saveEdit = async (expenseId: string) => {
    setSavingEdit(true);
    try {
      const updated = await updateExpense(travelRequestId, expenseId, {
        category: editCategory,
        amount: editAmount,
        currency: editCurrency,
        description: editDescription || undefined,
        date: editDate || undefined,
        ...(otherType ? otherBillPayload(otherType, editOther) : {})
      });
      onChanged(updated.expenses);
      setEditingId(null);
      setEditOriginal(null);
      toast.success("Bill updated");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not save changes to this bill. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (expenseId: string) => {
    try {
      const updated = await deleteExpense(travelRequestId, expenseId);
      onChanged(updated.expenses);
      setRemoveTarget(null);
      toast.success("Bill removed");
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Could not remove this bill - check your connection and try again."
      );
    }
  };

  // Groups consecutive pending cards that share the same underlying File -
  // every card split out of one multi-bill upload (see runAnalysis) reuses
  // the exact same File object reference, so grouping by reference equality
  // is reliable with no synthetic id needed. A manual bill (file: null) is
  // always its own group of one, since there's no filename to head it with.
  // Consecutive-only (not a global group-by) so removing/reordering cards
  // can't silently merge two visually-separated groups that happen to
  // share a file - in practice split cards are always adjacent anyway
  // (see runAnalysis's insertion logic).
  const pendingGroups = React.useMemo(() => {
    const groups: { file: File | null; bills: PendingBill[] }[] = [];
    for (const bill of pending) {
      const last = groups[groups.length - 1];
      if (bill.file && last?.file === bill.file) {
        last.bills.push(bill);
      } else {
        groups.push({ file: bill.file, bills: [bill] });
      }
    }
    return groups;
  }, [pending]);

  // A pending bill's receipt only exists as a client-side File - it has no
  // server id/expenseFileUrl yet (that only exists once uploadExpense()
  // actually commits it), so viewing it needs a local blob: URL instead.
  // One object URL per unique File (not per PendingBill - several bills can
  // share the same uploaded file, see pendingGroups above). Creating/
  // revoking URLs is a side effect, so it belongs in an effect, not a state
  // updater (which React can call more than once, e.g. under StrictMode,
  // and which must stay pure) - kept in a ref rather than state for that
  // reason, with `urlsVersion` as a plain render trigger so the JSX re-reads
  // the ref after the effect populates it (the ref's own mutation doesn't
  // itself cause a re-render - the first render after a new file arrives
  // would otherwise read an empty/stale map and link to nothing).
  const pendingFileUrlsRef = React.useRef<Map<File, string>>(new Map());
  const [, setUrlsVersion] = React.useState(0);
  React.useEffect(() => {
    const files = new Set(
      pending.map((b) => b.file).filter((f): f is File => f !== null)
    );
    const urls = pendingFileUrlsRef.current;
    let changed = false;
    for (const [file, url] of urls) {
      if (!files.has(file)) {
        URL.revokeObjectURL(url);
        urls.delete(file);
        changed = true;
      }
    }
    for (const file of files) {
      if (!urls.has(file)) {
        urls.set(file, URL.createObjectURL(file));
        changed = true;
      }
    }
    if (changed) setUrlsVersion((v) => v + 1);
  }, [pending]);
  // Unmount only - revokes whatever's left when the step itself goes away
  // (e.g. navigating off the wizard entirely), since the effect above only
  // ever reacts to `pending` changing, not to the component unmounting.
  React.useEffect(
    () => () => {
      for (const url of pendingFileUrlsRef.current.values())
        URL.revokeObjectURL(url);
    },
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-56">
          <Label>Upload Bills</Label>
          <p className="text-xs text-muted-foreground">
            Drop one or more receipts - each is read automatically and checked
            for duplicates before you add it.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={addManualBill}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Add bill manually
        </Button>
      </div>

      <Dropzone
        accept="image/*,.pdf"
        multiple
        onFilesSelected={onFilesSelected}
        maxSizeBytes={MAX_BILL_FILE_BYTES}
        onFilesRejected={onFilesRejected}
        allowCameraCapture
      />

      {pending.length > 0 && (
        <div className="space-y-3">
          {pendingGroups.map((group) => {
            const isMultiBillGroup = group.bills.length > 1;
            const duplicateCount = group.bills.filter(
              (b) => b.duplicateWarning
            ).length;
            // When EVERY bill split from this file is a duplicate, there's
            // nothing in the group worth keeping individually - one X on
            // the group header removes all of them together, and the
            // per-bill X's (below) are hidden. When only SOME are
            // duplicates, per-bill X's stay so the clean ones can be kept.
            const allBillsDuplicate =
              duplicateCount > 0 && duplicateCount === group.bills.length;
            return (
              <div
                key={group.bills[0].localId}
                className="space-y-3 rounded-md border border-primary bg-primary/5 p-3"
              >
                {/* Filename shown ONCE per group, not once per bill -
                    every card split out of the same uploaded file (see
                    runAnalysis's multi-bill detection) shares this header
                    instead of repeating the identical filename on each
                    card below it. Green-tinted (border-primary/bg-primary,
                    same "active" pattern used elsewhere e.g. Dropzone) so
                    the whole group reads as one uploaded-file unit at a
                    glance. */}
                <div className="-m-3 mb-0 flex items-center justify-between rounded-t-md bg-primary/10 px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
                    {group.bills.some(
                      (b) =>
                        b.state === "analyzing" ||
                        b.state === "checking-duplicate" ||
                        b.rechecking
                    ) && (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                    )}
                    {group.bills.every(
                      (b) =>
                        b.state === "ok" && !b.duplicateWarning && !b.rechecking
                    ) && (
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-primary" />
                    )}
                    {group.file ? (
                      <a
                        href={pendingFileUrlsRef.current.get(group.file)}
                        target="_blank"
                        rel="noreferrer"
                        className="min-w-0 flex-1 truncate text-primary underline underline-offset-2"
                      >
                        {group.file.name}
                      </a>
                    ) : (
                      <span className="min-w-0 flex-1 truncate">
                        Manual entry - no receipt attached
                      </span>
                    )}
                    {isMultiBillGroup && (
                      <span className="shrink-0 text-xs font-normal text-muted-foreground">
                        ({group.bills.length} bills found)
                      </span>
                    )}
                  </div>
                  {/* A single-bill group, or a multi-bill group where every
                      bill is duplicate-blocked, has one X that removes the
                      whole group at once. Otherwise (a multi-bill group
                      with a mix of clean and blocked bills) each bill below
                      gets its own X, since some might be worth keeping. */}
                  <div className="flex shrink-0 items-center">
                    {/* Manual bills (no file) are never duplicate-checked -
                        a multi-bill group re-checks per bill, in each
                        bill's own header row below. */}
                    {!isMultiBillGroup && group.file && (
                      <RecheckButton
                        bill={group.bills[0]}
                        onClick={() => recheckDuplicate(group.bills[0].localId)}
                      />
                    )}
                    {(!isMultiBillGroup || allBillsDuplicate) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          group.bills.forEach((b) => removePending(b.localId))
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* One combined, generic banner for the whole group instead
                    of repeating the same red box (or enumerating which
                    bill numbers) under every flagged bill's own fields. */}
                {duplicateCount > 0 &&
                  (() => {
                    const firstReason = group.bills.find(
                      (b) => b.duplicateWarning
                    )?.duplicateWarning;
                    return (
                      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-sm text-destructive">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        {/* min-w-0: without it, this flex child won't shrink
                         below its text's intrinsic width - on a narrow
                         phone viewport that pushed the second <p> to
                         overflow back over the first instead of wrapping
                         and stacking normally underneath it. */}
                        <div className="min-w-0">
                          <p className="break-words">
                            {isMultiBillGroup
                              ? allBillsDuplicate
                                ? `All ${duplicateCount} bills in this file look like duplicates. `
                                : `${duplicateCount} of ${group.bills.length} bills in this file look like duplicates. `
                              : ""}
                            {firstReason}
                          </p>
                          <p className="mt-1 break-words text-xs">
                            {allBillsDuplicate
                              ? "This file can't be added - remove it if already claimed elsewhere, or pick a different file. If something has changed (say the other copy was removed), use the refresh icon to check again."
                              : "The flagged bills can't be added - remove them individually if already claimed elsewhere, or pick a different file. If something has changed, use the refresh icon to check again."}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                <div
                  className={
                    isMultiBillGroup ? "space-y-4 divide-y" : undefined
                  }
                >
                  {group.bills.map((bill) => {
                    const needsConfirmation =
                      bill.state === "needs-confirmation";
                    const isDuplicateBlocked = !!bill.duplicateWarning;
                    return (
                      <div
                        key={bill.localId}
                        className={
                          isMultiBillGroup
                            ? "space-y-3 pt-4 first:pt-0"
                            : "space-y-3"
                        }
                      >
                        {isMultiBillGroup && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                              Bill {group.bills.indexOf(bill) + 1} of{" "}
                              {group.bills.length}
                            </span>
                            {/* Hidden when every bill in the group is
                                duplicate-blocked - the single X on the
                                group header above removes them all
                                together in that case. */}
                            <div className="flex items-center">
                              <RecheckButton
                                bill={bill}
                                onClick={() => recheckDuplicate(bill.localId)}
                              />
                              {!allBillsDuplicate && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => removePending(bill.localId)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        )}

                        {bill.state === "analyzing" && (
                          <p className="text-xs text-muted-foreground">
                            Analyzing with AI…
                          </p>
                        )}
                        {(bill.state === "checking-duplicate" ||
                          bill.rechecking) && (
                          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />{" "}
                            Checking for duplicates…
                          </p>
                        )}

                        {bill.note && (
                          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-sm text-amber-700 dark:text-amber-400">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span className="min-w-0 break-words">
                              {bill.note}
                            </span>
                          </div>
                        )}
                        {needsConfirmation && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => confirmLowConfidence(bill.localId)}
                          >
                            I've checked these fields - add anyway
                          </Button>
                        )}

                        {bill.state !== "analyzing" && (
                          <fieldset
                            disabled={
                              isDuplicateBlocked ||
                              bill.state === "checking-duplicate" ||
                              bill.rechecking
                            }
                            className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
                          >
                            <div className="space-y-1.5">
                              <Label>Category</Label>
                              <Select
                                value={bill.category}
                                onValueChange={(v) =>
                                  patchPending(bill.localId, {
                                    category: v as ExpenseCategory
                                  })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {categories.map((c) => (
                                    <SelectItem key={c} value={c}>
                                      {c}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label>Amount</Label>
                              <div className="flex gap-2">

                                <Select
                                  value={bill.currency}
                                  onValueChange={(v) =>
                                    patchPending(bill.localId, { currency: v })
                                  }
                                >
                                  <SelectTrigger className="w-24 shrink-0">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-64">
                                    {CURRENCIES.map((c) => (
                                      <SelectItem key={c} value={c}>
                                        {c}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <AmountInput
                                  value={bill.amount}
                                  onChange={(v) =>
                                    patchPending(bill.localId, { amount: v })
                                  }
                                />
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <Label>Bill date</Label>
                              <DatePicker
                                value={bill.date}
                                onChange={(v) =>
                                  patchPending(bill.localId, { date: v })
                                }
                                maxDate={new Date()}
                              />
                            </div>
                            {otherType && (
                              <OtherBillInputs
                                claimType={otherType}
                                value={bill.other}
                                onChange={(patch) =>
                                  patchPending(bill.localId, {
                                    other: { ...bill.other, ...patch }
                                  })
                                }
                                idPrefix={bill.localId}
                              />
                            )}
                            {/* A City travel bill has its own Purpose
                                instead of a description. */}
                            {otherType !== "city_travel" && (
                              <div className="space-y-1.5 sm:col-span-2">
                                <Label>Description (optional)</Label>
                                <Textarea
                                  value={bill.description}
                                  onChange={(e) =>
                                    patchPending(bill.localId, {
                                      description: e.target.value
                                    })
                                  }
                                  placeholder="e.g. Dinner with client"
                                />
                              </div>
                            )}
                          </fieldset>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex items-center gap-3">
            <Button onClick={submitAll} disabled={!canSubmit}>
              {submitting
                ? "Adding bills…"
                : `Add ${pending.length > 1 ? `all ${pending.length} bills` : "bill"}`}
            </Button>
            {blockedCount > 0 ? (
              <span className="text-xs text-muted-foreground">
                {blockedCount}{" "}
                {blockedCount === 1 ? "bill needs" : "bills need"} your
                attention above.
              </span>
            ) : readyToAdd.length < pending.length ? (
              <span className="text-xs text-amber-700 dark:text-amber-400">
                {missingDateOnly.length === pending.length - blockedCount
                  ? `Enter a bill date to add ${pending.length === 1 ? "this bill" : "these bills"}.`
                  : otherType
                    ? `Fill in the amount, bill date and every required field to add ${pending.length === 1 ? "this bill" : "these bills"}.`
                    : `Enter an amount greater than 0 and a bill date to add ${pending.length === 1 ? "this bill" : "these bills"}.`}
              </span>
            ) : null}
          </div>
          {/* A ready-to-add card (manual or uploaded, either claim method)
              left unsaved is a blocking condition, not a caption next to the
              button - NewRequest.tsx's Next is disabled for exactly this
              state (see its own comment on unaddedBills), so this has to
              actually read as "why can't I proceed" rather than a quiet
              aside someone scrolling past the last card's amount field could
              easily miss. Same bordered-banner treatment as the duplicate/
              needs-review warnings above, not a muted inline span. */}
          {blockedCount === 0 &&
            readyToAdd.length === pending.length &&
            !submitting && (
              <div className="flex items-start gap-2 rounded-md border border-amber-600/40 bg-amber-50 p-2.5 text-sm text-amber-800 dark:border-amber-400/40 dark:bg-amber-950/40 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  {pending.length === 1
                    ? "This bill hasn't been added yet."
                    : `These ${pending.length} bills haven't been added yet.`}{" "}
                  Click{" "}
                  <strong>
                    {pending.length === 1
                      ? "Add bill"
                      : `Add all ${pending.length} bills`}
                  </strong>{" "}
                  to save{" "}
                  {pending.length === 1 ? "it" : "them"} to this request, or
                  remove {pending.length === 1 ? "it" : "them"} with × above -
                  you can't continue until then.
                </p>
              </div>
            )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}

      {expenses.length > 0 && (
        <p className="text-xs font-medium text-muted-foreground">
          Uploaded bills ({expenses.length})
        </p>
      )}

      <div className="space-y-2">
        {expenses.map((expense) =>
          editingId === expense.id ? (
            <div
              key={expense.id}
              className="space-y-3 rounded-md border p-3 text-sm"
            >
              {expense.filename ? (
                <a
                  href={expenseFileUrl(travelRequestId, expense.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  {expense.filename}
                </a>
              ) : (
                <p className="text-muted-foreground">
                  No receipt attached (manual entry)
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select
                    value={editCategory}
                    onValueChange={(v) => setEditCategory(v as ExpenseCategory)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Amount</Label>
                  <div className="flex gap-2">
                    <Select
                      value={editCurrency}
                      onValueChange={setEditCurrency}
                    >
                      <SelectTrigger className="w-24 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <AmountInput value={editAmount} onChange={setEditAmount} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Bill date</Label>
                  <DatePicker value={editDate} onChange={setEditDate} maxDate={new Date()} />
                  {/* An older bill uploaded before the date became
                      mandatory can still show up with none - once it's
                      edited at all, though, a date must be set (FX
                      conversion needs it, see lib/fx.ts). */}
                </div>
                {otherType && (
                  <OtherBillInputs
                    claimType={otherType}
                    value={editOther}
                    onChange={(patch) => setEditOther((prev) => ({ ...prev, ...patch }))}
                    idPrefix={`edit-${expense.id}`}
                  />
                )}
                {otherType !== "city_travel" && (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Description (optional)</Label>
                    <Textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="e.g. Dinner with client"
                    />
                  </div>
                )}
                <div className="flex gap-2 sm:col-span-2">
                  <Button
                    size="sm"
                    onClick={() => saveEdit(expense.id)}
                    disabled={
                      savingEdit ||
                      !editIsDirty ||
                      !editDate ||
                      (otherType !== null && missingOtherBillField(otherType, editOther) !== null)
                    }
                  >
                    {savingEdit ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelEdit}
                    disabled={savingEdit}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div
              key={expense.id}
              // Phones: filename + edit/delete on the first line, category
              // and amount on the second, date on the third (order-*);
              // five fixed columns from sm up.
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-md border p-3 text-sm sm:grid-cols-[minmax(0,1fr)_8rem_7rem_5.5rem_4.5rem] sm:gap-y-0"
            >
              <div className="order-1 min-w-0 sm:order-none">
                {expense.filename ? (
                  <a
                    href={expenseFileUrl(travelRequestId, expense.id)}
                    target="_blank"
                    rel="noreferrer"
                    title={expense.filename}
                    className="block truncate text-primary underline"
                  >
                    {expense.filename}
                  </a>
                ) : (
                  <span className="block truncate text-muted-foreground">
                    No receipt attached
                  </span>
                )}
                {otherType && otherBillSummary(expense) && (
                  <span
                    className="block truncate text-xs text-muted-foreground"
                    title={otherBillSummary(expense)}
                  >
                    {otherBillSummary(expense)}
                  </span>
                )}
              </div>
              <div className="order-3 min-w-0 sm:order-none">
                <Badge
                  variant="outline"
                  className="min-w-0 max-w-full font-normal"
                  title={expense.category}
                >
                  <span className="min-w-0 truncate">{expense.category}</span>
                </Badge>
              </div>
              <span className="order-4 flex items-center justify-end gap-1 whitespace-nowrap text-right tabular-nums text-muted-foreground sm:order-none">
                {/* No per-bill "exceeds policy" warning here (unlike
                    DetailCard.tsx's read-only BillsSummaryCard, which keeps
                    it - that view has no sidebar to show the category
                    breakdown instead) - the Allowance Preview sidebar
                    already shows this bill's category total against its
                    limit live as it's added, so repeating the same warning
                    on every bill in an over-limit category here was
                    redundant clutter, not new information. */}
                {expense.currency} {expense.amount.toLocaleString()}
              </span>
              <span className="order-5 text-xs text-muted-foreground max-sm:col-span-2 sm:order-none sm:text-right">
                <span className="sm:hidden">Bill date: </span>
                {expense.date || "-"}
              </span>
              <div className="order-2 -my-1 flex justify-end gap-1 sm:order-none sm:my-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => startEdit(expense)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRemoveTarget(expense.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )
        )}
        {expenses.length === 0 && pending.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No bills uploaded yet.
          </p>
        )}
      </div>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove this bill?"
        description="This bill will be permanently removed from the request."
        confirmLabel="Remove"
        destructive
        onConfirm={() => removeTarget && remove(removeTarget)}
      />
    </div>
  );
}
